// src/encounter/lib/to-visual-acuity-request.ts
// Build the save body for the visual-acuity vital (PROD-1537). The visual-acuity reading is a
// PatientVital (LOINC 31479-8) whose `extraData` carries the VisualAcuityData (Flutter
// visual_acuity_form.dart: vital1.copyWith(extraData: ...) → the same CreateVitals/updateVitals
// save path). Pure — transport lives in use-save-vitals.ts.
import type { PatientVital } from '@/types/vitals-api';
import type { VitalsRequestContext, VitalsRequestResult } from './to-vitals-request';
import {
  type VisualAcuityData,
  isVisualAcuityEmpty,
  validateVisualAcuity,
  toVisualAcuityJson,
} from './visual-acuity';

/** LOINC for Visual Acuity (Flutter AppConstants.visualVitalId). */
export const VISUAL_ACUITY_LOINC = '31479-8';

const LOINC_SYSTEM = 'http://loinc.org';

function existingVisualAcuity(existing: PatientVital[] | null | undefined): PatientVital | undefined {
  return (existing ?? []).find((v) => !v.isDeleted && v.code?.code === VISUAL_ACUITY_LOINC);
}

/**
 * Resolve a VisualAcuityData into a CreateVitals/updateVitals body carrying the single
 * visual-acuity reading with `extraData`. Returns `{ error }` on an invalid pair, else
 * `{ mode, vitals }`. Round-trips the existing reading's identifire so the backend updates it in
 * place. Empty data is rejected (nothing to save).
 */
export function buildVisualAcuityRequest(
  data: VisualAcuityData,
  ctx: VitalsRequestContext,
): VitalsRequestResult {
  if (isVisualAcuityEmpty(data)) return { error: 'Enter at least one visual-acuity value.' };
  const error = validateVisualAcuity(data);
  if (error) return { error };

  const prev = existingVisualAcuity(ctx.existing);
  const reading: PatientVital = {
    identifire: prev?.identifire ?? null,
    status: 'final',
    code: { code: VISUAL_ACUITY_LOINC, display: 'Visual Acuity', system: LOINC_SYSTEM },
    // The vital's own quantity is unused for visual acuity (the values live in extraData); send an
    // empty quantity carrying the code, matching the other readings' shape.
    quantity: { value: null, unit: null, code: VISUAL_ACUITY_LOINC },
    subject: ctx.patientId,
    encounter: ctx.encounterId,
    category: 'vital-signs',
    effectiveDateTime: ctx.now,
    extraData: toVisualAcuityJson(data),
    isDeleted: false,
  };
  return { mode: prev ? 'update' : 'create', vitals: [reading] };
}
